import type { CropRect } from "./cropGeometry";

export const UNREADABLE_IMAGE_MESSAGE =
  "We couldn't open that image. Try another file.";

export class ImageReadError extends Error {
  constructor(message = UNREADABLE_IMAGE_MESSAGE) {
    super(message);
    this.name = "ImageReadError";
  }
}

/**
 * Decodes a file with its EXIF orientation applied, so a phone photo taken
 * sideways is upright everywhere it is used — both in the crop stage and in
 * the exported file. Without this the two disagree.
 */
export async function decodeImage(file: Blob): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    throw new ImageReadError();
  }
}

export interface CropExportTarget {
  width: number;
  height: number;
  /** PNG keeps transparency; JPEG flattens it onto white. */
  type: "image/png" | "image/jpeg";
  /** JPEG only. */
  quality?: number;
}

/** Draws the framed area at exactly the target size. */
export async function exportCroppedImage(
  source: CanvasImageSource,
  rect: CropRect,
  target: CropExportTarget,
): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = target.width;
  canvas.height = target.height;

  const context = canvas.getContext("2d");
  if (!context) throw new ImageReadError();

  // JPEG has no alpha: without a ground, a PNG logo's transparent areas come out black.
  if (target.type === "image/jpeg") {
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, target.width, target.height);
  }

  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(
    source,
    rect.x,
    rect.y,
    rect.width,
    rect.height,
    0,
    0,
    target.width,
    target.height,
  );

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, target.type, target.quality);
  });
  if (!blob) throw new ImageReadError();
  return blob;
}
